import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Edit, CheckCircle, X, Loader2, FileText } from 'lucide-react';
import { RichTextEditor } from '../RichTextEditor';
import { updateQuote, getQuoteNoteTemplates } from '../../services/api';

export function NotesSection({ quote, quoteId, refetch, isLocked }) {
  const [editing, setEditing] = useState(false);
  const [notesContent, setNotesContent] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const { data: templates = [] } = useQuery({ queryKey: ['quoteNoteTemplates'], queryFn: () => getQuoteNoteTemplates().then(r => r.data) });

  React.useEffect(() => {
    if (quote) setNotesContent(quote.notes || '');
  }, [quote]);

  const handleSave = async () => {
    try {
      await updateQuote(quoteId, { notes: notesContent });
      refetch();
      setEditing(false);
      toast.success('Notes saved');
    } catch { toast.error('Failed to save notes'); }
  };

  const applyTemplate = (template) => {
    setNotesContent(template.body);
    setShowTemplates(false);
    toast.success(`"${template.title}" template applied`);
  };

  const hasNotes = notesContent && notesContent.trim() && notesContent !== '<p><br></p>';

  return (
    <div className="border-t mt-2" data-testid="quote-notes-section">
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <h6 className="font-bold text-slate-800">NOTES</h6>
          {!isLocked && !editing && (
            <div className="flex gap-2 print:hidden">
              <div className="relative">
                <button onClick={() => setShowTemplates(!showTemplates)} className="px-3 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300 flex items-center gap-1" data-testid="template-selector-btn">
                  <FileText size={12} /> Apply Template
                </button>
                {showTemplates && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white border rounded-lg shadow-lg z-20" data-testid="template-dropdown">
                    {templates.length === 0 ? (
                      <div className="p-3 text-sm text-slate-400">No templates. Add them in Email Settings.</div>
                    ) : templates.map(t => (
                      <button key={t.id} onClick={() => applyTemplate(t)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-cyan-50 border-b last:border-b-0 transition-colors"
                        data-testid={`apply-template-${t.id}`}>
                        <span className="font-medium text-slate-700">{t.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setEditing(true)} className="px-3 py-1 text-xs bg-yellow-400 text-slate-800 rounded hover:bg-yellow-500" data-testid="edit-notes-btn">
                Edit Notes
              </button>
            </div>
          )}
        </div>
        {editing ? (
          <div className="space-y-2 print:hidden">
            <RichTextEditor value={notesContent} onChange={setNotesContent} />
            <div className="flex justify-between items-center">
              <div className="relative">
                <button onClick={() => setShowTemplates(!showTemplates)} className="px-3 py-1.5 text-xs bg-slate-200 rounded hover:bg-slate-300 flex items-center gap-1" data-testid="load-template-in-editor">
                  <FileText size={12} /> Apply Template
                </button>
                {showTemplates && (
                  <div className="absolute left-0 bottom-full mb-1 w-64 bg-white border rounded-lg shadow-lg z-20" data-testid="template-dropdown-editor">
                    {templates.length === 0 ? (
                      <div className="p-3 text-sm text-slate-400">No templates. Add them in Email Settings.</div>
                    ) : templates.map(t => (
                      <button key={t.id} onClick={() => applyTemplate(t)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-cyan-50 border-b last:border-b-0 transition-colors"
                        data-testid={`apply-template-editor-${t.id}`}>
                        <span className="font-medium text-slate-700">{t.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setNotesContent(quote?.notes || ''); setEditing(false); setShowTemplates(false); }} className="px-4 py-1.5 text-sm border rounded">Cancel</button>
                <button onClick={handleSave} className="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-hover" data-testid="save-notes-btn">Save Notes</button>
              </div>
            </div>
          </div>
        ) : hasNotes ? (
          <div className="prose prose-sm max-w-none text-slate-600 overflow-hidden break-words" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notesContent) }} />
        ) : (
          <p className="text-sm text-slate-400 italic print:hidden">No notes added. Click "Apply Template" or "Edit Notes" to add notes.</p>
        )}
      </div>
    </div>
  );
}



export function TermsOfTrade() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-8 border-t pt-6" data-testid="terms-of-trade">
      <div className="flex items-center justify-between mb-3 print:hidden">
        <h3 className="font-bold text-sm text-slate-700">Terms of Trade</h3>
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary hover:underline" data-testid="toggle-terms">
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      <div className={`text-[10px] leading-relaxed text-slate-500 space-y-4 ${expanded ? '' : 'hidden'} print:block`}>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">1. Definitions</h4>
          <p>Company: Project Maintenance Perth. Customer: Person or Company buying goods/services. Products and/or services: Items/work purchased. Contract: Agreement between the company and customer. Contract price: Agreed price for goods and services.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">2. Quotation</h4>
          <p>Quotes provided within 7 days of request. Customer advised to check quotations carefully; additional work is at customer expense. Quotes valid for 30 days (unless otherwise agreed). Quotes are non-negotiable (unless discount offered). Signed copy required before work commences.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">3. Price</h4>
          <p>Price as indicated on invoices. Prices subject to material cost increases after quote date. Prices based on mid-range colours; some selections may increase price. Surcharge possible for alterations to specifications after order. Inadequate surface substrate may incur additional labour/material costs.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">4. Acceptance</h4>
          <p>Receiving an instruction constitutes acceptance of terms and conditions. Deposit of 25% of the total quotation is payable upon acceptance. Company will exercise care to minimise property damage. Start/completion dates subject to weather conditions.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">5. Payment</h4>
          <p>Unless stated otherwise, payment due in full within 7 days of work completion. Interest of 2% per month charged on late payments. Company can require security for payment and withhold delivery/work. Customer pays all costs and expenses incurred by the company in enforcing these conditions, including legal costs.</p>
          <p className="font-bold mt-1">Electronic Bank Payment Details: Account Name: Project Painters Perth Pty Ltd, BSB: 016281, Account Number: 289 864 693</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">6. Paint Products</h4>
          <p>Company accepts no responsibility for colour choice. Customer incurs costs for any colour changes after selection. Colour matching aims for best possible match but may not be 100%. No responsibility for yellowing of gloss enamel products.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">7. Safety</h4>
          <p>Health, safety, and welfare of all persons on site is paramount. Safe working conditions aligned with Australian Law.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">8. Dispute Resolution</h4>
          <p>Company will endeavour to resolve disputes without court proceedings.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">9. Reservation of Title</h4>
          <p>Ownership of goods remains with the company until the full purchase price and all other monies are paid.</p>
        </div>
        <div>
          <h4 className="font-bold text-[11px] text-slate-700 mb-1">10. Warranty</h4>
          <p>Five (5) year warranty on workmanship and products used. Warranties subject to property owner carrying out general maintenance. No responsibility for work altered, touched up, or tampered with by a third party. Claims must be made in writing within 10 days of completion.</p>
        </div>

        {/* Acceptance / Signature Section */}
        <div className="mt-6 border-t pt-4">
          <p className="text-[11px] text-slate-700 mb-4">I/We have read and accept the Terms of Trade as outlined in this document.</p>
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-b border-slate-300 pb-1"><span className="text-[10px] text-slate-400">Customer Signature</span></div>
              <div className="border-b border-slate-300 pb-1"><span className="text-[10px] text-slate-400">Print Name</span></div>
              <div className="border-b border-slate-300 pb-1"><span className="text-[10px] text-slate-400">Date</span></div>
            </div>
            <div className="space-y-6">
              <div className="border-b border-slate-300 pb-1"><span className="text-[10px] text-slate-400">Business (if applicable)</span></div>
              <div className="border-b border-slate-300 pb-1"><span className="text-[10px] text-slate-400">Print Name</span></div>
              <div className="border-b border-slate-300 pb-1"><span className="text-[10px] text-slate-400">Date</span></div>
            </div>
          </div>
        </div>
      </div>

      {!expanded && (
        <p className="text-xs text-slate-400 italic print:hidden">Terms of Trade are included when printing or generating PDF. Click "Expand" to preview.</p>
      )}
    </div>
  );
}


